import React, { FormEvent } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { getCurrentYear, useUniqueWcagValues } from 'common-react';
import { Button, Card, NumberInput, Switch, Tooltip, Typography } from 'ui-components';
import {
  CALC_SAVINGS_TEST_ID,
  RETIREMENT_CALCULATOR_NAMESPACE,
  RETIREMENT_CALCULATOR_STEPS_COUNT,
  RetirementCalculatorStep,
  SavingsField,
  SAVINGS_PRODUCT_LABELS,
  SAVINGS_PRODUCT_RULES,
  SAVINGS_PRODUCTS_ORDER,
} from '../../tools/RetirementCalculator.const';
import { RetirementCalculatorSavingsPropsModel } from '../../tools/RetirementCalculator.model';
import { formatAmount, getYearlyLimitForecast } from '../../tools/RetirementCalculator.utils';
import { useSavingsForm } from '../../tools/useSavingsForm';

import useStyles from './RetirementCalculatorSavings.style';

export const RetirementCalculatorSavings: React.FC<RetirementCalculatorSavingsPropsModel> = ({
  titleId,
  title,
  submitLabel,
  initialValues,
  yearlyContributionLimits,
  onSubmit,
  onValuesChange,
  disabled = false,
  isLoading = false,
  className,
}) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const { getUniqueValue } = useUniqueWcagValues();
  const { values, toggleProduct, setAmount, submit } = useSavingsForm({ initialValues, onSubmit, onValuesChange });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const amountFields = [
    {
      field: SavingsField.ACCUMULATED,
      label: t('savings.accumulated.label'),
      placeholder: t('savings.accumulated.placeholder'),
    },
    {
      field: SavingsField.MONTHLY_CONTRIBUTION,
      label: t('savings.monthly-contribution.label'),
      placeholder: t('savings.monthly-contribution.placeholder'),
    },
  ];

  return (
    <Card className={clsx(c.root, className)} size="big" testId="RetirementCalculatorSavings">
      <form className={c.form} onSubmit={handleSubmit} noValidate={true} autoComplete="off">
        <Typography
          variant="h1"
          component="h1"
          fontWeight="extraBold"
          textColor="maroons.100"
          id={titleId}
          className={c.title}
          testId={`${CALC_SAVINGS_TEST_ID}-title`}
        >
          {title ?? t('title')}
        </Typography>

        <div className={c.stepHeader}>
          <Typography
            variant="subHeadline2"
            component="p"
            fontWeight="bold"
            textColor="oranges.50"
            className={c.stepHeading}
          >
            {t('savings.step-label', {
              step: RetirementCalculatorStep.SAVINGS,
              total: RETIREMENT_CALCULATOR_STEPS_COUNT,
            })}
          </Typography>
          <Typography
            variant="subHeadline2"
            component="h2"
            fontWeight="bold"
            className={c.stepHeading}
          >
            {t('savings.subtitle')}
          </Typography>
        </div>

        <ul className={c.productList}>
          {SAVINGS_PRODUCTS_ORDER.map(product => {
            const productId = getUniqueValue(product);
            const label = t(`savings.products.${product}`, SAVINGS_PRODUCT_LABELS[product]);
            const tooltip = t(`savings.tooltips.${product}`);
            const { enabled } = values[product];

            const limitForecast = SAVINGS_PRODUCT_RULES[product].hasYearlyLimit
              ? getYearlyLimitForecast(
                  values[product][SavingsField.MONTHLY_CONTRIBUTION],
                  yearlyContributionLimits?.[product],
                )
              : undefined;
            const limitWarning =
              enabled && limitForecast?.isExceeded
                ? t('savings.limit-exceeded', {
                    product: label,
                    year: getCurrentYear(),
                    yearlyLimit: formatAmount(limitForecast.yearlyLimit),
                    remainingMonths: limitForecast.remainingMonths,
                    forecast: formatAmount(limitForecast.forecast),
                    monthlyLimit: formatAmount(limitForecast.monthlyLimit ?? 0),
                  })
                : undefined;

            return (
              <li key={product} className={c.product}>
                <div className={c.productHeader}>
                  <span className={c.productLabel}>
                    <Typography
                      variant="body1"
                      component="span"
                      fontWeight="bold"
                      id={`${productId}-label`}
                    >
                      {label}
                    </Typography>
                    {tooltip && <Tooltip title={tooltip} className={c.productTooltip} />}
                  </span>
                  <Switch
                    name={productId}
                    checked={enabled}
                    onChange={event => toggleProduct(product, event.target.checked)}
                    inputProps={{
                      id: productId,
                      'aria-labelledby': `${productId}-label`,
                      'data-testid': `${CALC_SAVINGS_TEST_ID}-switch-${product}`,
                    }}
                  />
                </div>

                <div className={clsx(c.amountsWrapper, !enabled && c.amountsWrapperHidden)}>
                  <div className={c.amountsClip}>
                    <div className={c.amounts}>
                      {amountFields.map(({ field, label: amountLabel, placeholder }) => {
                        const showLimitWarning =
                          field === SavingsField.MONTHLY_CONTRIBUTION && !!limitWarning;

                        return (
                          <div key={field} className={c.amountField}>
                            <NumberInput
                              id={`${productId}-${field}`}
                              name={`${product}-${field}`}
                              className={c.amountInput}
                              label={amountLabel}
                              adornment="PLN"
                              placeholder={placeholder}
                              decimalScale={2}
                              fixedDecimalScale={false}
                              value={values[product][field]}
                              onValueChange={value => setAmount(product, field, value)}
                              disabled={!enabled}
                              testId={`${CALC_SAVINGS_TEST_ID}-${product}-${field}`}
                            />
                            {showLimitWarning && (
                              <Tooltip
                                type="info-orange"
                                open={true}
                                title={limitWarning}
                                className={c.limitTooltip}
                                testId={`${CALC_SAVINGS_TEST_ID}-${product}-limit-warning`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <Button
          type="submit"
          className={c.submitButton}
          disabled={disabled}
          isLoading={isLoading}
          testId={`${CALC_SAVINGS_TEST_ID}-submit`}
        >
          {submitLabel ?? t('savings.submit')}
        </Button>
      </form>
    </Card>
  );
};
